import DataType from '@opsbase/lib-datatype/DataType';
import Palette from '@opsbase/lib-palette';
import useIndustries from '@opsbase/react-gql-hooks/industry/useIndustries';
import useTemplates from '@opsbase/react-gql-hooks/template/useTemplates';
import useCss from '@opsbase/web-css';
import DataChooserValueList from '@opsbase/web-data-choosers/DataChooser/DataChooserValueList';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import SearchBox from '@opsbase/web-textfield/SearchBox';
import Spinner from '@opsbase/web-ui/Spinner';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TemplateChooserList from './TemplateChooserList';
import TemplateList from './TemplateList';
// eslint-disable-next-line no-shadow
var Mode;
(function (Mode) {
    Mode["SOURCE"] = "SOURCE";
    Mode["CATEGORY"] = "CATEGORY";
    Mode["INDUSTRY"] = "INDUSTRY";
})(Mode || (Mode = {}));
const makeMyClasses = () => ({
    root: {
        overflow: 'hidden',
        borderRadius: 3,
        border: `1px solid ${Palette.InputBorderGrey.main}`,
        backgroundColor: Palette.White.main,
        minHeight: 350,
        maxHeight: 370,
    },
});
export function TemplateChooser(props) {
    const { selectedItems = [], onChange } = props;
    const [keyword, setKeyword] = useState('');
    const [mode, setMode] = useState();
    const [level2Choice, setLevel2Choice] = useState();
    const myCss = useCss(makeMyClasses);
    useEffect(() => {
        if (mode === Mode.SOURCE)
            return;
        setKeyword('');
    }, [mode]);
    useEffect(() => {
        if (!level2Choice)
            return;
        setKeyword('');
    }, [level2Choice]);
    // Load all the data
    const { loading: loadingTemplates, data: templates } = useTemplates({
        fields: 'id, title, description, source, categories, industries { id }',
    });
    const { loading: loadingIndustries, data: industries } = useIndustries();
    const sources = useMemo(() => loadingTemplates
        ? []
        : uniq(templates.map(t => t.source).filter(s => !!s)).map(v => ({
            id: v,
            title: v,
        })), [loadingTemplates, templates]);
    const categories = useMemo(() => loadingTemplates
        ? []
        : uniq(flatten(templates.map(t => t.categories)).filter(s => !!s)).map(v => ({
            id: v,
            title: v,
        })), [loadingTemplates, templates]);
    const loading = loadingTemplates || loadingIndustries;
    // Build Level 1 Menu
    const level1Data = useMemo(() => [
        { title: 'Browse by source', id: Mode.SOURCE },
        { title: 'Browse by category', id: Mode.CATEGORY },
        { title: 'Browse by industry', id: Mode.INDUSTRY },
    ], []);
    // Build Level 2 Menu
    const level2Data = useMemo(() => mode === Mode.SOURCE
        ? sources
        : mode === Mode.CATEGORY
            ? categories
            : mode === Mode.INDUSTRY
                ? industries
                : [], [mode, categories, industries, sources]);
    // Build Level 3 Menu
    const level3Data = keyword
        ? templates.filter((t) => (t.title && t.title.toLowerCase().indexOf(keyword.toLowerCase()) > -1) ||
            (t.description &&
                t.description.toLowerCase().indexOf(keyword.toLowerCase()) > -1))
        : !mode || !level2Choice
            ? []
            : templates.filter(t => {
                var _a, _b;
                switch (mode) {
                    case Mode.SOURCE:
                        return t.source === level2Choice || false;
                    case Mode.CATEGORY:
                        return ((_a = t.categories) === null || _a === void 0 ? void 0 : _a.some(v => v === level2Choice)) || false;
                    case Mode.INDUSTRY:
                        return ((_b = t.industries) === null || _b === void 0 ? void 0 : _b.some(v => v.id === level2Choice)) || false;
                }
                return false;
            });
    const handleSelectItem = useCallback(item => {
        const selectedWithout = selectedItems.filter(i => i.id !== item.id);
        if (selectedWithout.length < selectedItems.length) {
            onChange(selectedWithout);
        }
        else {
            onChange([...selectedItems, item]);
        }
    }, [onChange, selectedItems]);
    const handleChangeKeyword = useCallback(value => {
        setMode(undefined);
        setLevel2Choice(undefined);
        setKeyword(value);
    }, []);
    return (React.createElement(Spinner, { busy: loading }, () => (React.createElement(Vertical, { gutter: true, hidden: true },
        React.createElement(Horizontal, { classes: { root: myCss.root } },
            React.createElement(Pane, { paddingX: true, style: {
                    borderRight: `1px solid ${Palette.InputBorderGrey.main}`,
                } },
                React.createElement(SearchBox, { keyword: keyword, autoFocus: true, onChange: handleChangeKeyword }),
                React.createElement(TemplateChooserList, { data: level1Data, selected: [mode], onSelect: setMode })),
            !!level2Data.length && (React.createElement(TemplateChooserList, { data: level2Data, selected: [level2Choice], onSelect: setLevel2Choice })),
            React.createElement(TemplateList, { data: level3Data, selected: selectedItems, onSelect: handleSelectItem })),
        React.createElement(DataChooserValueList, { required: true, hideSelectButton: true, type: DataType.TEMPLATE, value: selectedItems, onChange: onChange })))));
}
export default TemplateChooser;
